import { AxiosError } from 'axios';
import { authHeader } from 'services';
import {
  ICreateTreatmentConsultation,
  IFindTreatmentConsultationParams,
  ITreatmentConsultation,
  ITreatmentConsultationExam,
  ITreatmentConsultationResult,
} from 'types/TreatmentConsultation';

import { Api } from './api/axios.service';

const BASE_URL = '/consultation';

/**
 * Create or update a Consultation
 *
 * @param {ICreateTreatmentConsultation} data
 * @return {*}
 */
const saveConsultation = async (data: ICreateTreatmentConsultation) => {
  try {
    if (data.consultType === 'teleconsultation') {
      data.orientation = '';
    } else {
      data.diagnosis = '';
      data.treatment = '';
      data.prescription = [];
    }

    if (data.id) {
      return await Api.put(`${BASE_URL}/${data.id}`, data, {
        headers: authHeader(),
      });
    }

    await Api.post(BASE_URL, data, {
      headers: authHeader(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível salvar a consulta'
    );
  }
};

/**
 * Save or update a Prescription
 *
 * @param {(Pick<ICreateTreatmentConsultation, 'id' | 'prescription'>)} data
 * @return {*}
 */
const savePrescription = async (
  data: Pick<ICreateTreatmentConsultation, 'id' | 'prescription'>
) => {
  try {
    return await Api.put(`${BASE_URL}/${data.id}`, data, {
      headers: authHeader(),
    });
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível salvar a prescrição'
    );
  }
};

/**
 * Find all Consultations by Pet Id
 *
 * @param {IFindTreatmentConsultationParams} {
 *   petId,
 *   limit,
 *   page,
 *   startDate,
 *   endDate,
 * }
 * @return {*}  {Promise<ITreatmentConsultationResult>}
 */
const findAllByPetId = async ({
  petId,
  limit = 3,
  page = 1,
  startDate,
  endDate,
}: IFindTreatmentConsultationParams): Promise<ITreatmentConsultationResult> => {
  try {
    const { data } = await Api.get(`${BASE_URL}/findAllByPetId/${petId}`, {
      headers: authHeader(),
      params: {
        limit,
        page,
        startDate,
        endDate,
      },
    });

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível listar as consultas'
    );
  }
};

/**
 * Find one treatment consultation by treatment on going id
 *
 * @param {string} treatmentOnGoingId
 * @return {*}  {Promise<ITreatmentConsultation>}
 */
const findByTreatmentOnGoingId = async (
  treatmentOnGoingId: string
): Promise<ITreatmentConsultation> => {
  try {
    const { data } = await Api.get(
      `${BASE_URL}/findByTreatmentOnGoingId/${treatmentOnGoingId}`,
      {
        headers: authHeader(),
      }
    );

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível buscar a consulta'
    );
  }
};

/**
 * list all available exams
 *
 * @return {*}  {Promise<
 *   ITreatmentConsultationExam[]
 * >}
 */
const findAllExams = async (): Promise<ITreatmentConsultationExam[]> => {
  try {
    const { data } = await Api.get(`${BASE_URL}/findAllExams/`, {
      headers: authHeader(),
    });

    return data;
  } catch (reason) {
    const { response } = reason as AxiosError<{ error: string }>;

    throw new Error(
      response?.data?.error || 'Não foi possível buscar os medicamentos'
    );
  }
};

export const TreatmentConsultationService = {
  saveConsultation,
  savePrescription,
  findAllByPetId,
  findByTreatmentOnGoingId,
  findAllExams,
};
